import { forwardRef, type ReactNode } from 'react';

import { cva } from 'cva';
import {
  Button as RACButton,
  type ButtonProps as RACButtonProps,
} from 'react-aria-components/Button';

import { ProgressCircle } from '../progress_circle/progress_circle';

import styles from './button.module.css';

export const buttonVariants = cva({
  base: styles.button,
  variants: {
    size: {
      s: styles.size_s,
      m: styles.size_m,
      l: styles.size_l,
    },
    variant: {
      primary: styles.primary,
      secondary: styles.secondary,
      tertiary: styles.tertiary,
      destructive: styles.destructive,
      brand: styles.brand,
    },
    round: {
      true: styles.round,
    },
    fullWidth: {
      true: styles.full_width,
    },
    withIcon: {
      true: styles.with_icon,
    },
    withEndIcon: {
      true: styles.with_end_icon,
    },
    iconOnly: {
      true: styles.icon_only,
    },
    isActive: {
      true: styles.is_active,
    },
  },
  defaultVariants: {
    size: 'm',
    variant: 'primary',
  },
});

export type ButtonVariantProps = {
  size?: 's' | 'm' | 'l';
  variant?: 'primary' | 'secondary' | 'tertiary' | 'destructive' | 'brand';
  round?: boolean;
  fullWidth?: boolean;
  icon?: ReactNode;
  endIcon?: ReactNode;
  children?: ReactNode;
  pendingLabel?: string;
};

export type ButtonProps = RACButtonProps & ButtonVariantProps;

/**
 * Button component that can be used to trigger actions (using onPress), or
 * submit forms (using type="submit").
 *
 * Use `LinkButton` for navigation, and `ToggleButton` for a togglable button.
 *
 * See https://react-spectrum.adobe.com/react-aria/Button.html for the underlying
 * implementation.
 *
 * ```tsx
 * <Button variant="primary" size="m">
 *  Click me!
 * </Button>
 * ```
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      size = 'm',
      variant = 'primary',
      children,
      round,
      fullWidth,
      className,
      icon,
      endIcon,
      pendingLabel = 'Pending…',
      ...props
    },
    ref,
  ) => {
    const hasChildren = Boolean(children);
    const classes = buttonVariants({
      size,
      variant,
      round,
      fullWidth,
      className,
      withIcon: hasChildren && Boolean(icon),
      withEndIcon: hasChildren && Boolean(endIcon),
      iconOnly: !hasChildren && Boolean(icon),
    });
    return (
      <RACButton ref={ref} {...props} className={classes}>
        {({ isPending }) => (
          <>
            {icon}
            {children}
            {isPending ? (
              <div className={styles.progress_circle_wrapper}>
                <ProgressCircle
                  aria-label={pendingLabel}
                  className={styles.progress_circle}
                  size="xs"
                />
              </div>
            ) : null}
            {endIcon}
          </>
        )}
      </RACButton>
    );
  },
);
